import { useCallback, useEffect, useRef, useState } from 'react';
import { normalizeTranscript } from '@evi/shared';

/**
 * Browser speech-recognition (the listening half of EVII voice mode). A thin
 * wrapper over the Web Speech API `SpeechRecognition`, which is free and runs in
 * the browser -- no service, no cost. It listens continuously and ends the turn
 * itself only after a short silence (so a natural mid-sentence pause does not cut
 * the user off), fires the final transcript, and the chat's loop restarts it
 * after EVII has spoken.
 *
 * `supported` is false where the API is absent (notably Firefox) so the chat can
 * hide the mic. Recognition is created lazily and torn down on unmount.
 *
 * The SpeechRecognition types are not in TS's lib.dom, so the minimal shapes are
 * declared here (module-scoped) and the constructor is read off `window` with a
 * cast -- no global augmentation, no dependency on the lib having them.
 */

interface SpeechRecognitionAlternativeLike {
  readonly transcript: string;
  readonly confidence: number;
}
interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [index: number]: SpeechRecognitionAlternativeLike;
}
interface SpeechRecognitionResultListLike {
  readonly length: number;
  readonly [index: number]: SpeechRecognitionResultLike;
}
interface SpeechRecognitionEventLike extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultListLike;
}
interface SpeechRecognitionErrorEventLike extends Event {
  readonly error: string;
}
interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface UseSpeechRecognition {
  /** False where the Web Speech API is unavailable (e.g. Firefox). */
  supported: boolean;
  /** True between a successful start and the recognizer ending. */
  listening: boolean;
  /** Live partial transcript for the current utterance (cleared on final/stop). */
  interimText: string;
  start: () => void;
  stop: () => void;
}

/**
 * Merge one recognition result's transcript into the accumulator, robust to two
 * engine behaviors:
 *   - Standard browsers: each FINAL result is a DISTINCT new segment, so they
 *     are concatenated into the full sentence.
 *   - Samsung Galaxy Chrome: final results are CUMULATIVE overlapping snapshots
 *     of the SAME growing utterance -- the results list grows with repeated or
 *     extending copies (captured on-device: finals "tell me", "tell me", "tell
 *     me about" as the list grew). Blindly summing them stacks duplicates
 *     ("tell metell metell me"); taking the fuller snapshot yields it once.
 * Rule: if one of (acc, seg) is a PREFIX of the other they are snapshots of the
 * same text -> keep the longer; identical -> keep acc; otherwise distinct ->
 * concatenate. Comparison ignores edge whitespace; the kept string preserves the
 * engine's own spacing. Only prefix relationships merge, so genuinely distinct
 * segments are never mis-merged on a mid-word coincidence.
 */
function mergeSegment(acc: string, seg: string): string {
  if (!seg.trim()) return acc;
  if (!acc) return seg;
  const a = acc.trim();
  const s = seg.trim();
  if (a === s) return acc;            // identical snapshot
  if (s.startsWith(a)) return seg;    // seg extends acc (cumulative) -> take longer
  if (a.startsWith(s)) return acc;    // seg is a shorter prefix snapshot -> keep acc
  return acc + seg;                   // genuinely distinct segment -> concatenate
}

export function useSpeechRecognition(
  onFinalTranscript: (text: string) => void,
  lang?: string,
): UseSpeechRecognition {
  const ctorRef = useRef<SpeechRecognitionCtor | null>(null);
  if (ctorRef.current === null) ctorRef.current = getRecognitionCtor();
  const supported = ctorRef.current !== null;

  const [listening, setListening] = useState(false);
  const [interimText, setInterimText] = useState('');

  const recogRef = useRef<SpeechRecognitionLike | null>(null);
  // Latest callback without re-binding the recognizer's handlers.
  const cbRef = useRef(onFinalTranscript);
  useEffect(() => { cbRef.current = onFinalTranscript; }, [onFinalTranscript]);

  // Latest recognition language (BCP-47) without re-binding handlers; the fresh
  // recognizer built per session in start() reads this. Kept in sync with the
  // web hook. The admin app has no per-user locale today, so callers pass
  // nothing and this stays en-US — ready for admin i18n without a hook change.
  const langRef = useRef(lang ?? 'en-US');
  useEffect(() => { langRef.current = lang ?? 'en-US'; }, [lang]);

  // The CURRENT turn's transcript (settled finals + the live interim), REBUILT
  // from the results list on every event -- never appended. Plus the silence
  // timer that ends the turn, and two lifecycle guards that make duplication
  // impossible regardless of how the engine behaves:
  //   - runningRef: at most ONE recognition session at a time, so a redundant or
  //     racing start() can never run two recognizers that both accumulate.
  //   - firedRef: the turn's transcript fires EXACTLY once (the silence timer OR
  //     onend, never both -- the trailing onresult the engine emits while
  //     stopping cannot re-send it).
  // A FRESH recognizer is built per session in start(), so the engine's results
  // list always starts empty and nothing can carry across a stop -> start.
  const finalTextRef = useRef('');
  const silenceTimerRef = useRef<number | null>(null);
  const runningRef = useRef(false);
  const firedRef = useRef(false);

  const clearSilence = useCallback((): void => {
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    // Exactly one session at a time. A restart only proceeds once the previous
    // session's onend has cleared runningRef, so two recognizers never overlap.
    if (runningRef.current) return;
    const Ctor = ctorRef.current;
    if (!Ctor) return;

    // Detach + abort any prior (already-ended) recognizer so it can never fire
    // into our state, then build a FRESH one with a fresh, empty results list.
    const prev = recogRef.current;
    if (prev) {
      prev.onstart = null;
      prev.onresult = null;
      prev.onerror = null;
      prev.onend = null;
      try { prev.abort(); } catch { /* ignore */ }
    }

    // How long the user may be silent before the turn is treated as finished --
    // longer than a natural mid-sentence pause, so pausing to think does not end
    // the turn, only a real stop does.
    const SILENCE_TIMEOUT_MS = 1500;
    const r = new Ctor();
    r.lang = langRef.current;
    // continuous:true keeps the session open ACROSS pauses (it does NOT end on
    // the first end-of-speech); we decide the turn is over via the silence timer.
    r.continuous = true;
    r.interimResults = true;
    r.maxAlternatives = 1;

    // Fire the finished turn's transcript EXACTLY once. firedRef guards both the
    // silence-timer path and the onend path, so the trailing onresult the engine
    // emits while stopping (which re-arms the timer) can never send a 2nd time.
    const fireTurn = (): void => {
      if (firedRef.current) return;
      firedRef.current = true;
      const finished = finalTextRef.current.trim();
      finalTextRef.current = '';
      setInterimText('');
      if (finished) cbRef.current(finished);
    };
    // (Re)start the finish countdown. Any speech resets it; when it elapses with
    // no new speech, the turn is done -> fire once + stop the session.
    const armSilence = (): void => {
      clearSilence();
      silenceTimerRef.current = window.setTimeout(() => {
        silenceTimerRef.current = null;
        fireTurn();
        try { r.stop(); } catch { /* ignore */ }
      }, SILENCE_TIMEOUT_MS);
    };

    r.onstart = () => setListening(true);
    r.onresult = (e) => {
      // Build the transcript from THIS session's results, robust to TWO engine
      // behaviors. mergeSegment concatenates genuinely distinct segments
      // (standard browsers) but DEDUPES cumulative overlapping snapshots: Samsung
      // Galaxy Chrome emits multiple FINAL results that each repeat/extend the
      // same growing phrase ("tell me", "tell me", "tell me about"), and blindly
      // summing them stacked duplicates ("tell metell me..."). Taking the fuller
      // snapshot instead yields the phrase once. (A fresh recognizer per session
      // already means no PRIOR session's snapshots are in this list.)
      let finalText = '';
      let interim = '';
      for (let i = 0; i < e.results.length; i += 1) {
        const res = e.results[i];
        const text = res?.[0]?.transcript ?? '';
        if (res?.isFinal) finalText = mergeSegment(finalText, text);
        else interim = mergeSegment(interim, text);
      }
      // Same normalisation as the driver hook, from the SAME shared table —
      // these two hooks are duplicated implementations, and a second copy of
      // the mapping is how they would drift.
      finalTextRef.current = normalizeTranscript(mergeSegment(finalText, interim));
      setInterimText(interim);
      // Speech happened -> restart the finish countdown; do NOT fire yet.
      armSilence();
    };
    // Errors (no-speech, not-allowed, network) end the session; onend follows.
    r.onerror = () => { clearSilence(); };
    r.onend = () => {
      clearSilence();
      // Session is over -> allow the next start() to open a fresh one.
      runningRef.current = false;
      setListening(false);
      // Fire if the silence timer didn't (e.g. the browser ended the session on
      // its own). firedRef makes this a no-op when the timer already fired.
      fireTurn();
      finalTextRef.current = '';
      setInterimText('');
    };

    // New session: clear the buffer + guards BEFORE starting so nothing carries
    // over from a previous turn.
    clearSilence();
    runningRef.current = true;
    firedRef.current = false;
    finalTextRef.current = '';
    setInterimText('');
    recogRef.current = r;
    try {
      r.start();
    } catch {
      // Could not start (rare) -- drop the flag so a later start() can retry.
      runningRef.current = false;
    }
  }, [clearSilence]);

  const stop = useCallback(() => {
    clearSilence();
    // Explicit stop = cancel: suppress the turn fire (firedRef) + drop buffered
    // text so onend sends nothing. runningRef is cleared by onend when the
    // session actually ends, so a restart racing ahead of onend can't overlap.
    firedRef.current = true;
    finalTextRef.current = '';
    const r = recogRef.current;
    if (r) {
      try { r.stop(); } catch { /* ignore */ }
    }
    setListening(false);
    setInterimText('');
  }, [clearSilence]);

  // Tear down on unmount: clear the timer, drop handlers, abort any session.
  useEffect(() => () => {
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    runningRef.current = false;
    const r = recogRef.current;
    if (r) {
      r.onstart = null;
      r.onresult = null;
      r.onerror = null;
      r.onend = null;
      try { r.abort(); } catch { /* ignore */ }
    }
  }, []);

  return { supported, listening, interimText, start, stop };
}
